import { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { authService } from 'services';
import { _Object, showTostify } from 'utils/interfaces';
import CustomToast from 'views/components/form-inputs/alert';
import { InputText } from 'primereact/inputtext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { setLoggedInUser } from '../../../../redux/slices/session.slice';
import PhoneInput from 'react-phone-input-2';
import { Avatar } from 'primereact/avatar';
import '../edit-profile/style.scss';
import ChangePassword from 'views/components/change-password';
import { AvatarPlaceholder } from 'assets/images';
import { FileUpload } from 'primereact/fileupload';
import axios from 'axios';
import store from 'store';

const ProfileForm = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toastRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const { loggedInUser } = useSelector((state: _Object) => state.session);
  const [visible, setVisible] = useState(false);
  const [img, setImg] = useState<any>(null);

  useEffect(() => {
    if (loggedInUser?.id) {
      setInitialData({
        first_name: loggedInUser?.first_name || '',
        last_name: loggedInUser?.last_name,
        phone_number1: loggedInUser?.phone_number1,
        email: loggedInUser?.email,
        gender: loggedInUser.gender,
        job_type: loggedInUser.job_type,
        address1: loggedInUser.address1,
        city: loggedInUser.city,
        state: loggedInUser.state,
        postcode: loggedInUser.postcode,
        country: loggedInUser.country,
        company_name: loggedInUser.company_name,
        company_idnumber: loggedInUser.company_idnumber
      });
    } else {
      dispatch(setLoggedInUser());
    }
  }, [loggedInUser?.id]);

  const [initialData, setInitialData] = useState<_Object>({
    first_name: loggedInUser?.first_name || '',
    last_name: loggedInUser?.last_name,
    phone_number1: loggedInUser?.phone_number1,
    email: loggedInUser?.email,
    gender: loggedInUser.gender,
    job_type: loggedInUser.job_type,
    address1: loggedInUser.address1,
    city: loggedInUser.city,
    state: loggedInUser.state,
    postcode: loggedInUser.postcode,
    country: loggedInUser.country,
    company_name: loggedInUser.company_name,
    company_idnumber: loggedInUser.company_idnumber
  });

  const formik: _Object = useFormik({
    initialValues: initialData,
    enableReinitialize: true,

    validationSchema: Yup.object({
      first_name: Yup.string().required('First name is required'),
      last_name: Yup.string().required('Last name is required'),
      phone_number1: Yup.string().required('Primary phone number is required'),
      email: Yup.string().email('Invalid email').required('Email is required'),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      const formattedValues = {
        first_name: values.first_name,
        last_name: values.last_name,
        gender: values.gender,
        email: values.email,
        phone_number1: values.phone_number1,
        job_type: values.job_type,
        address1: values.address1,
        city: values.city,
        state: values.state,
        postcode: values.postcode,
        country: values.country,
        company_name: values.company_name,
        company_idnumber: values.company_idnumber
      };

      authService.editProfile(formattedValues).then((result: _Object) => {
        setLoading(false);
        if (!result.error) {
          authService.getMe().then((result: _Object) => {
            setInitialData(result);
            dispatch(setLoggedInUser());
            showTostify(toastRef, 'success', 'Profile updated successfully');
            onUpload()
          });
        } else {
          showTostify(toastRef, 'error', result.message);
        }
      });
    },
  });

  const onUpload = async () => {
    if (img) {
      setLoading(true);
      const data: any = new FormData();
      data.append('key', 'profile_picture');
      data.append('entity_id', loggedInUser?.id);
      data.append('entity_type', 'user');
      data.append('files', img);
      try {
        const response = await axios.post(`${process.env.REACT_APP_BASE_URL}/v1/admin/media`, data, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': store.get(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'bIESIcg42Wb9') }
        });
        if (!response?.data?.error) {
          showTostify(toastRef, 'success', response?.data?.message);
          dispatch(setLoggedInUser());
          setLoading(false);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        alert(error);
      }
    }
  };

  const handleFileSelect = (e: any) => {
    setImg(e.files[0]);
  };

  const userProfileImage = () => {
    const x = loggedInUser?.media?.length > 0 ? loggedInUser?.media[loggedInUser?.media?.length - 1].path : AvatarPlaceholder
    return x
  }

  return (
    <>
      <div className='edit-profile'>
        <CustomToast ref={toastRef} />
        <div className='card bg-white p-4' style={{ width: '397px', margin: "0 auto" }}>
          <div className='flex align-items-center justify-content-center mb-4 profileImageWrapper'>
            <Avatar image={img ? URL.createObjectURL(img) : userProfileImage()} style={{ width: '100px', height: "100px" }} shape="circle" className='relative'>
              <FileUpload
                mode="basic"
                name="profileImage"
                accept="image/*"
                maxFileSize={1000000}
                onSelect={handleFileSelect}
              />
              {/* <Button icon="pi pi-pencil" link type="button" severity="danger"
               className='absolute text-white min-h-auto border-circle' /> */}
            </Avatar>
          </div>

          <form onSubmit={formik.handleSubmit}>
            <div className="form-group">
              <label htmlFor="first_name">First name<span className='required-field'>*</span></label>
              <InputText
                name="first_name"
                className='form-control'
                placeholder="Enter here"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.first_name}
              />
              {formik.touched.first_name && formik.errors.first_name ? (
                <Message severity="error" text={formik.errors.first_name} />
              ) : null}
            </div>

            <div className="form-group">
              <label htmlFor="last_name">Last name<span className='required-field'>*</span></label>
              <InputText
                name="last_name"
                className='form-control'
                placeholder="Enter here"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.last_name}
              />
              {formik.touched.last_name && formik.errors.last_name ? (
                <Message severity="error" text={formik.errors.last_name} />
              ) : null}
            </div>

            <div className="form-group">
              <label htmlFor="email">Email<span className='required-field'>*</span></label>
              <InputText
                id="email"
                name="email"
                className='form-control'
                placeholder="Enter here"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.email}
              />
              {formik.touched.email && formik.errors.email ? (
                <Message severity="error" text={formik.errors.email} />
              ) : null}
            </div>

            <div className="form-group">
              <label htmlFor="username">Phone<span className='required-field'>*</span></label>
              <PhoneInput
                country={'us'}
                value={formik.values.phone_number1}
                onChange={(phone) => {
                  formik.setFieldValue('phone_number1', phone || '')
                }}
                onBlur={formik.handleBlur}
                placeholder="Enter here"
                />
              {formik.touched.phone_number1 && formik.errors.phone_number1 ? (
                <Message severity="error" text={formik.errors.phone_number1} />
              ) : null}
            </div>

            <Button type="submit"
              disabled={!formik.isValid || formik.isSubmitting || loading}
              loading={loading}
              label="Update Profile" className='primary w-full ' />
          </form>

          <div className="footer flex justify-content-center mt-4">
            <p className='m-0 flex align-items-center'>
              Do you want to &nbsp;
              <Button
                link
                onClick={() => {
                  setVisible(true)
                }}
                className='p-0'
              >
                Change Password?
              </Button>
            </p>
          </div>
          <ChangePassword setVisible={setVisible} visible={visible} />
        </div>
      </div>
    </>
  );
};

export default ProfileForm;
